import * as React from 'react'
import { ReportType } from '@/app/constants/reportType'
import { OutputType } from '@/app/constants/outputType'
import { useGeneratePrintout } from '../hooks/useGeneratePrintout'
import { PrintActionButton } from '../components/PrintActionButton'
import { PrintModal, PrintOptions } from '../components/PrintModal'

interface Props {
  tooltip?: string
  companyId: string
  dateFrom?: string
  dateTo?: string
  driverIds: string[]
  availableReports: ReportType[]
  outputType?: OutputType

  // optional controlled state
  isOpen?: boolean
  setOpen?: (open: boolean) => void
}

export const PrintActionContainer: React.FC<Props> = ({
  tooltip,
  companyId,
  dateFrom,
  dateTo,
  driverIds,
  availableReports,
  outputType,
  isOpen: controlledOpen,
  setOpen: controlledSetOpen,
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false)
  const isControlled =
    controlledOpen !== undefined && controlledSetOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen
  const setOpen = isControlled ? controlledSetOpen : setInternalOpen
  const [sending, setSending] = React.useState<boolean>(false)

  const { handleGeneratePrintout, isGenerating } = useGeneratePrintout()

  const onGenerate = async (
    selectedReports: ReportType[],
    options: PrintOptions,
  ) => {
    if (sending) return

    setSending(true)
    try {
      await handleGeneratePrintout({
        companyId,
        driverIds,
        dateFrom: options.dateFrom || dateFrom,
        dateTo: options.dateTo || dateTo,
        selectedReports,
        generateToSeparateFiles: options.generateToSeparateFiles,
        rememberSelected: options.rememberSelected,
        issuerSignature: options.issuerSignature,
        driverSignature: options.driverSignature,
        outputType,
      })
      setOpen(false)
    } catch (error) {
      console.error('Error generating printout:', error)
    } finally {
      setSending(false)
    }
  }

  return (
    <>
      {!isControlled && (
        <PrintActionButton
          tooltip={tooltip}
          onClick={() => setOpen(true)}
          disabled={isGenerating}
        />
      )}

      <PrintModal
        pending={sending || isGenerating}
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        availableReports={availableReports}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onGenerate={onGenerate}
      />
    </>
  )
}
